import { MakeGenerics, Route } from "@tanstack/react-location";

import { lazy } from "react";
import { ExchangeMode } from "../stores";
import { EOrderType } from "../stores/OrderContext";
import { featureIsEnabled } from "../utils/features.utils";
import Home from "./Home";
import { NotFound } from "./NotFound";
import TasteTestDetailWrapper from "./TasteTestDetail/TasteTestDetailWrapper";
import YieldFarming from "./YieldFarming/YieldFarming";

export const paths = {
  root: "/",
  exchange: `/exchange`,
  liquidity: {
    root: "/liquidity",
    children: {
      provide: "/provide",
      withdraw: "/withdraw",
      zap: "/zap",
      createPool: "/create-pool",
    },
  },
  tasteTest: {
    root: "/discover",
    detail: "/discover/:slug",
  },
  orders: "/orders",
  yieldFarming: "/yield-farming",
};

export const externalRoutes = {
  about: "https://sundae.fi/",
  disclaimer: "https://sundae.fi/terms",
  discord: "https://discord.gg/Sundae",
  explorer: {
    mainnet: {
      cardanoscan: "https://cardanoscan.io",
      cexplorer: "https://cexplorer.io",
    },
    preview: {
      cardanoscan: "https://preview.cardanoscan.io",
      cexplorer: "https://preview.cexplorer.io",
    },
  },
  faq: "https://help.sundaeswap.finance/en/",
  faqFarms: "https://help.sundaeswap.finance/en/collections/3299695-yield-farming",
  feesHelpArticle:
    "https://help.sundaeswap.finance/en/articles/7338192-minimum-ada-available-balance",
  governance: "https://governance.sundaeswap.finance/",
  handlePersonalization: "https://medium.com/ada-handle/how-to-personalization-e8368e7566d7",
  hotjar: "https://www.hotjar.com/",
  learnMoreAboutWallets: "https://help.sundaeswap.finance/en/collections/3303291-wallets",
  medium: "https://sundaeswap-finance.medium.com/",
  privacy: "https://sundae.fi/privacy",
  tasteTestArticle: "https://sundae.fi/posts/sundaeswap-presents-the-taste-test",
  terms: "https://sundae.fi/terms",
  risksAndRewards:
    "https://help.sundaeswap.finance/en/articles/5887307-what-are-the-risks-and-rewards",
  twitter: "https://twitter.com/SundaeSwap",
  liquidity: "https://help.sundaeswap.finance/en/collections/3281728-liquidity",
  cardanoTokenRegistryHelp:
    "https://help.sundaeswap.finance/en/articles/7336428-the-cardano-token-registry",
  cryptoTaxCalculatorManual: "https://app.cryptotaxcalculator.io/imports/custom/manual",
  cryptoTaxCalculatorAssistance: "https://cryptotaxcalculator.io/guides/advanced-manual-csv-import",
  cip45VideoDemonstration: "https://youtu.be/mRpXIh-DyYM?si=bcPCfNhGMQryTpCX",
  cip45LearnMore: "https://github.com/fabianbormann/CIPs/blob/master/CIP-%3F%3F%3F%3F/README.md",
};

export enum PortfolioTab {
  assets = "assets",
  liquidity = "liquidity",
  yieldFarming = "yieldFarming",
}

export type TLocationGenerics = MakeGenerics<{
  LoaderData: {};
  Search: {
    given?: string;
    taken?: string;
    mode?: ExchangeMode;
    order?: EOrderType;
    tab?: PortfolioTab;
    zapAssetId?: string;
    zapPoolId?: string;
  };
}>;

const LazyExchange = lazy(() => import("./Exchange"));
const LazyLiquidity = lazy(() => import("./Liquidity"));
const LazyTasteTest = lazy(() => import("./TasteTest"));
const LazyTasteTestDetail = lazy(() => import("./TasteTestDetail"));
const LazyOrders = lazy(() => import("./Orders"));

export const AppRoutes: Route[] = [
  {
    path: paths.root,
    element: <Home />,
  },
  {
    path: paths.exchange,
    element: <LazyExchange />,
  },
  {
    path: paths.liquidity.root,
    children: [
      {
        path: "/",
        element: <LazyLiquidity />,
      },
    ],
  },
  ...(featureIsEnabled("tasteTest")
    ? [
        {
          path: paths.tasteTest.root,
          children: [
            {
              path: "/",
              element: <LazyTasteTest />,
            },
            {
              path: "/:slug",
              element: (
                <TasteTestDetailWrapper>
                  <LazyTasteTestDetail />
                </TasteTestDetailWrapper>
              ),
            },
          ],
        },
      ]
    : []),
  {
    path: paths.orders,
    element: <LazyOrders />,
  },
  {
    path: paths.yieldFarming,
    element: <YieldFarming />,
  },
  {
    element: <NotFound className="lg:min-h-[calc(100vh-254px)]" />,
  },
];
